import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useParams } from "wouter";
import { Loader2, Calendar, User, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { RelatedTopics } from "@/components/blog/RelatedTopics";
import { Button } from "@/components/ui/button";
import { useState } from "react";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [userInterests, setUserInterests] = useState<string[]>([]);
  
  // Fetch blog post by slug
  const { data, isLoading, error } = useQuery({
    queryKey: ['/api/blog/slug', slug],
    queryFn: () => getQueryFn({ on401: "throw" })(`/api/blog/slug/${slug}`),
    enabled: !!slug
  });
  
  // If loading or error
  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error || !data?.post) {
    return (
      <div className="container mx-auto py-12 text-center">
        <h1 className="text-4xl font-bold tracking-tight mb-3">
          Post Not Found
        </h1>
        <p className="text-xl text-muted-foreground mb-6">
          We couldn't find the blog post you're looking for.
        </p>
        <Button asChild>
          <a href="/blog">Back to Blog</a>
        </Button>
      </div>
    );
  }
  
  const { post } = data;
  
  // Add tag to user interests when clicked
  const handleTagClick = (tag: string) => {
    if (!userInterests.includes(tag)) {
      setUserInterests([...userInterests, tag]);
    }
  };
  
  return (
    <div className="container mx-auto py-12">
      <div className="max-w-4xl mx-auto">
        {/* Post Header */}
        <div className="mb-8">
          <Badge variant="outline" className="mb-3">
            {post.category}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                {new Date(post.publishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{post.author}</span>
            </div>
          </div>
          
          {/* Featured Image */}
          {post.imageUrl && (
            <div className="rounded-lg overflow-hidden mb-8">
              <img 
                src={post.imageUrl} 
                alt={post.title} 
                className="w-full h-auto object-cover" 
              />
            </div>
          )}
        </div>
        
        {/* Post Content */}
        <div className="prose prose-lg max-w-none mb-12">
          {/* Display the content - in a real app, you might use a Markdown renderer here */}
          <p className="text-xl text-muted-foreground mb-8">
            {post.summary}
          </p>
          <div>
            {post.content.split('\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </div>
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-3">Topics</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Badge 
                  key={tag} 
                  variant="secondary" 
                  className="cursor-pointer"
                  onClick={() => handleTagClick(tag)}
                >
                  <Tag className="h-3 w-3 mr-1" /> 
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* AI-powered related topics */}
        <RelatedTopics postId={post.id} />
        
        {/* AI-powered related posts */}
        <RelatedPosts 
          currentPostId={post.id} 
          userInterests={userInterests}
        />
      </div>
    </div>
  );
}